import React, { useState, useEffect, useMemo, useCallback } from "react";
import { fmtDate, serverFetch, useGetIsDesktop } from "@extensions";
import { useUser } from "@redux";
import { message, Spin, Card, Button, Space, Popconfirm, Table } from "antd";
import Payment from "../payment/payment";
import dayjs from "dayjs";
import "./sales.css";

const SalesUnpaid = ({ refreshKey = 0, onLoadingChange }) => {
  const user = useUser();
  const isDesktop = useGetIsDesktop();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);

  useEffect(() => {
    onLoadingChange?.(loading);
    return () => onLoadingChange?.(false);
  }, [loading, onLoadingChange]);

  const fetchSales = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await serverFetch("OnlineClient/Sales", { }, user);
      setSales(data || []);
    } catch (error) {
      console.error("Ошибка получения продаж:", error);
      message.error(error?.UserMessage || "Не удалось загрузить список покупок");
      setSales([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchSales();
    }
  }, [user, refreshKey]);

  const totalAmount = useMemo(() => {
    return sales.reduce((sum, sale) => sum + (sale.syma || 0), 0);
  }, [sales]);

  const formatDate = (dateStr) => {
    return fmtDate(dateStr) || "";
  };

  const formatDocDate = (dateStr) => {
    if (!dateStr) return "";
    const date = dayjs(dateStr);
    const today = dayjs();
    if (date.isSame(today, 'day')) {
      return date.format("HH:mm:ss");
    }
    return date.format("DD.MM.YYYY HH:mm:ss");
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    return dayjs(timeStr, "HH:mm:ss").format("HH:mm");
  };

  const formatPrice = (price) => {
    if (!price) return "0";
    return price.toLocaleString("ru-RU");
  };

  const formatRecord = (sale) => {
    if (!sale.recordDate) return "";
    const date = formatDate(sale.recordDate);
    const start = sale.recordStart ? formatTime(sale.recordStart) : "";
    const finish = sale.recordFinish ? formatTime(sale.recordFinish) : "";
    if (start && finish) {
      return `${date} ${start}-${finish}`;
    }
    return date;
  };

  const handleDelete = useCallback(async (saleId) => {
    try {
      await serverFetch(`OnlineClient/Sale/${saleId}`, { method: "DELETE" }, user);
      message.success("Продажа успешно удалена");
      fetchSales(); // Обновляем список продаж
    } catch (error) {
      console.error("Ошибка удаления продажи:", error);
      message.error(error?.userMessage || "Не удалось удалить продажу");
    }
  }, [user, fetchSales]);

  const columns = useMemo(() => [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Дата",
      dataIndex: "docDate",
      key: "docDate",
      width: 120,
      render: (date) => formatDate(date),
    },
    {
      title: "Наименование",
      dataIndex: "productName",
      key: "productName",
    },
    {
      title: "Кол.",
      dataIndex: "qty",
      key: "qty",
      width: 80,
      align: "right",
    },
    {
      title: "Сумма",
      dataIndex: "syma",
      key: "syma",
      width: 120,
      align: "right",
      render: (price) => `${formatPrice(price)} руб.`,
    },
    {
      title: "Сотрудник",
      dataIndex: "recordUserName",
      key: "recordUserName",
      width: 150,
    },
    {
      title: "Запись",
      key: "record",
      width: 200,
      render: (_, record) => formatRecord(record),
    },
    {
      title: "Оплатить до",
      dataIndex: "paymentDeadline",
      key: "paymentDeadline",
      width: 150,
      render: (date) => date ? formatDocDate(date) : "",
    },
    {
      title: "Действия",
      key: "actions",
      width: 120,
      align: "center",
      render: (_, record) => (
        record.canBeDeletedByClient ? (
          <Popconfirm
            title="Удалить продажу?"
            description="Вы уверены, что хотите удалить эту продажу?"
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button type="link" danger size="small">
              Удалить
            </Button>
          </Popconfirm>
        ) : null
      ),
    },
  ], [handleDelete]);

  const handlePay = () => {
    setPaymentOpen(true);
  };

  return (
    <div className="sales-content">
      {loading ? (
        <div className="sales-loading">
          <Spin size="large" />
        </div>
      ) : isDesktop ? (
        <>
          {sales.length > 0 ? (
            <Table
              columns={columns}
              dataSource={sales}
              rowKey="id"
              pagination={false}
              className="sales-table"
            />
          ) : (
            <div className="sales-empty">Нет покупок</div>
          )}
          {sales.length > 0 && (
            <div className="sales-total">
              <Space size="large" style={{ width: "100%", justifyContent: "space-between" }}>
                <div className="sales-total-text">К оплате: {formatPrice(totalAmount)} руб.</div>
                <Button type="primary" onClick={handlePay}>Оплатить</Button>
              </Space>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="sales-list">
            {sales.length > 0 ? (
              sales.map((sale) => (
                <Card key={sale.id} className="sales-card" size="small">
                  <div className="sales-card-content">
                    <div className="sales-card-header">
                      <div className="sales-card-date">{formatDate(sale.docDate)}</div>
                      <div className="sales-card-price">{formatPrice(sale.syma)} руб.</div>
                    </div>
                    {sale.deleted && <span className="sales-card-deleted">Удалено</span>}
                    <div className="sales-card-body">
                      <div className="sales-card-info">
                        {sale.productName && <div className="sales-card-product">{sale.productName}</div>}
                        {sale.qty && (
                          <div className="sales-card-qty">Количество: {sale.qty}</div>
                        )}
                        {sale.recordUserName && (
                          <div className="sales-card-employee">Сотрудник: {sale.recordUserName}</div>
                        )}
                        {sale.recordDate && (
                          <div className="sales-card-record">Запись: {formatRecord(sale)}</div>
                        )}
                        {sale.paymentDeadline && (
                          <div className="sales-card-deadline">Оплатить до: {formatDocDate(sale.paymentDeadline)}</div>
                        )}
                      </div>
                      {sale.canBeDeletedByClient && (
                        <div className="sales-card-actions">
                          <Popconfirm
                            title="Удалить продажу?"
                            description="Вы уверены, что хотите удалить эту продажу?"
                            onConfirm={() => handleDelete(sale.id)}
                            okText="Да"
                            cancelText="Нет"
                          >
                            <Button type="link" danger size="small">
                              Удалить
                            </Button>
                          </Popconfirm>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))
            ) : (
              <div className="sales-empty">Нет покупок</div>
            )}
          </div>
          {sales.length > 0 && (
            <div className="sales-total">
              <Space size="large" style={{ width: "100%", justifyContent: "space-between" }}>
                <div className="sales-total-text">К оплате: {formatPrice(totalAmount)} руб.</div>
                <Button type="primary" onClick={handlePay}>Оплатить</Button>
              </Space>
            </div>
          )}
        </>
      )}
      <Payment sales={sales} open={paymentOpen} onClose={() => setPaymentOpen(false)}/>
    </div>
  );
};

export default SalesUnpaid;

